import { useCallback } from "react";
import { useKeyAction } from "hooks/useKeyAction";
import { isFormControlFocused } from "utils/isFormControlFocused";
import type { UseKeyboardNavigationArgs } from "./types";

export const useKeyboardNavigation = ({
  goPrev,
  goNext,
  isDisabled,
}: UseKeyboardNavigationArgs): void => {
  // Arrows inside an input or textarea move the caret, not the selection.
  const handlePrev = useCallback(
    (event: KeyboardEvent) => {
      if (isFormControlFocused(event)) return;
      goPrev();
    },
    [goPrev],
  );

  const handleNext = useCallback(
    (event: KeyboardEvent) => {
      if (isFormControlFocused(event)) return;
      goNext();
    },
    [goNext],
  );

  // Up and Down would scroll the page; that is only prevented when the key navigates.
  const handlePrevVertical = useCallback(
    (event: KeyboardEvent) => {
      if (isFormControlFocused(event)) return;
      event.preventDefault();
      goPrev();
    },
    [goPrev],
  );

  const handleNextVertical = useCallback(
    (event: KeyboardEvent) => {
      if (isFormControlFocused(event)) return;
      event.preventDefault();
      goNext();
    },
    [goNext],
  );

  useKeyAction({
    code: "ArrowLeft",
    callback: handlePrev,
    options: { isDisabled },
  });

  useKeyAction({
    code: "ArrowUp",
    callback: handleNextVertical,
    options: { isDisabled },
  });

  useKeyAction({
    code: "ArrowRight",
    callback: handleNext,
    options: { isDisabled },
  });

  useKeyAction({
    code: "ArrowDown",
    callback: handlePrevVertical,
    options: { isDisabled },
  });
};
